import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  detectDeviceLanguage, 
  createTranslator, 
  normalizeLanguage,
  type SupportedLanguage 
} from '@/lib/i18n';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => Promise<void>;
  t: (key: string, variables?: Record<string, any>) => string;
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// localStorage key for language preference
const STORAGE_KEY = 'cashee-language';

// ✅ Synchronous initial language (prevents FOUC)
const getInitialLanguage = (): SupportedLanguage => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return normalizeLanguage(stored);
    }
  } catch (e) {
    console.warn('Failed to read language from localStorage:', e);
  }
  return detectDeviceLanguage();
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  // ✅ Initialize with localStorage/navigator synchronously
  const [language, setLanguageState] = useState<SupportedLanguage>(getInitialLanguage);
  const [translator, setTranslator] = useState(() => createTranslator(getInitialLanguage()));
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Update <html lang> attribute
  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  // ✅ Re-create translator when language changes
  useEffect(() => {
    const newTranslator = createTranslator(language);
    setTranslator(() => newTranslator);
  }, [language]);

  // Load language from Supabase (if user is logged in)
  useEffect(() => {
    loadLanguageFromSupabase();
  }, []);

  const loadLanguageFromSupabase = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data } = await supabase
          .from('budget_settings')
          .select('language')
          .eq('user_id', user.id)
          .single();
        
        if (data?.language) {
          const dbLang = normalizeLanguage(data.language);
          setLanguageState(dbLang);
          localStorage.setItem(STORAGE_KEY, dbLang);
        } else {
          // Save detected language to DB
          const detected = language; // already set from localStorage/navigator
          await supabase
            .from('budget_settings')
            .upsert({
              user_id: user.id,
              language: detected,
              detected_language: detected
            });
        }
      }
    } catch (error) {
      console.error('Failed to load language from Supabase:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setLanguage = useCallback(async (lang: SupportedLanguage) => {
    const normalizedLang = normalizeLanguage(lang);
    
    setLanguageState(normalizedLang);
    
    // ✅ Persist to localStorage immediately
    try {
      localStorage.setItem(STORAGE_KEY, normalizedLang);
    } catch (e) {
      console.warn('Failed to save language to localStorage:', e);
    }
    
    // Persist to Supabase (if logged in)
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('budget_settings')
          .upsert({
            user_id: user.id,
            language: normalizedLang
          });
      }
    } catch (error) {
      console.error('Failed to save language to Supabase:', error);
    }
  }, []);

  // ✅ Stable `t` function with useCallback
  const t = useCallback(
    (key: string, variables?: Record<string, any>) => {
      return translator(key, variables);
    },
    [translator]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, isLoading }),
    [language, setLanguage, t, isLoading]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
